import { ref, type Ref } from 'vue';

import { createSingleton } from '@deepidoo/deesplay/utils';

export type Languages = 'de-DE' | 'en-US' | 'es-ES' | 'fr-FR' | 'it-IT' | 'pt-PT' | 'zh-CN';
type Keys = 'report-dialog.title' |
            'report.properties.status' |
            'report.properties.category' |
            'report.properties.title' |
            'report.properties.description' |
            'report.properties.page-url' |
            'report.properties.client' |
            'report.properties.user' |
            'report.properties.browser' |
            'report.properties.operating-system' |
            'report.properties.window-size' |
            'report.properties.created-at' |
            'report.category.bug' |
            'report.category.design' |
            'report.category.question' |
            'report.category.request' |
            'report.category.wording' |
            'report.status.closed' |
            'report.status.in-progress' |
            'report.status.non-reproductible' |
            'report.status.pending' |
            'report.status.resolved' |
            'report.status.wont-fix' |
            'report.conversation.title' |
            'report.conversation.placeholder';

const translations: Record<Languages, Record<Keys, string>> = {
    'en-US': {
        'report-dialog.title': 'Reports',
        'report.properties.status': 'Status',
        'report.properties.category': 'Category',
        'report.properties.title': 'Title',
        'report.properties.description': 'Description',
        'report.properties.page-url': 'Page URL',
        'report.properties.client': 'Client',
        'report.properties.user': 'User',
        'report.properties.browser': 'Browser',
        'report.properties.operating-system': 'Operating system',
        'report.properties.window-size': 'Window size',
        'report.properties.created-at': 'Created at',
        'report.category.bug': 'Bug',
        'report.category.design': 'Design',
        'report.category.question': 'Question',
        'report.category.request': 'Request',
        'report.category.wording': 'Wording',
        'report.status.closed': 'Closed',
        'report.status.in-progress': 'In progress',
        'report.status.non-reproductible': 'Not reproducible',
        'report.status.pending': 'Pending',
        'report.status.resolved': 'Resolved',
        'report.status.wont-fix': 'Won\'t fix',
        'report.conversation.title': 'Conversation',
        'report.conversation.placeholder': 'Enter a message (Press Shift + Enter for a new line)...'
    },
    'fr-FR': {
        'report-dialog.title': 'Rapports',
        'report.properties.status': 'Statut',
        'report.properties.category': 'Catégorie',
        'report.properties.title': 'Titre',
        'report.properties.description': 'Description',
        'report.properties.page-url': 'URL de la page',
        'report.properties.client': 'Client',
        'report.properties.user': 'Utilisateur',
        'report.properties.browser': 'Navigateur',
        'report.properties.operating-system': 'Système d\'exploitation',
        'report.properties.window-size': 'Taille de la fenêtre',
        'report.properties.created-at': 'Créé le',
        'report.category.bug': 'Bug',
        'report.category.design': 'Design',
        'report.category.question': 'Question',
        'report.category.request': 'Requête',
        'report.category.wording': 'Texte',
        'report.status.closed': 'Fermé',
        'report.status.in-progress': 'En cours',
        'report.status.non-reproductible': 'Non reproductible',
        'report.status.pending': 'En attente',
        'report.status.resolved': 'Résolu',
        'report.status.wont-fix': 'Refusé',
        'report.conversation.title': 'Conversation',
        'report.conversation.placeholder': 'Entrez un message (Appuyez sur Shift + Entrée pour une nouvelle ligne)...'
    },
    'de-DE': {
        'report-dialog.title': 'Berichte',
        'report.properties.status': 'Status',
        'report.properties.category': 'Kategorie',
        'report.properties.title': 'Titel',
        'report.properties.description': 'Beschreibung',
        'report.properties.page-url': 'Seiten-URL',
        'report.properties.client': 'Client',
        'report.properties.user': 'Benutzer',
        'report.properties.browser': 'Browser',
        'report.properties.operating-system': 'Betriebssystem',
        'report.properties.window-size': 'Fenstergröße',
        'report.properties.created-at': 'Erstellt am',
        'report.category.bug': 'Fehler',
        'report.category.design': 'Design',
        'report.category.question': 'Frage',
        'report.category.request': 'Anfrage',
        'report.category.wording': 'Schreibweise',
        'report.status.closed': 'Geschlossen',
        'report.status.in-progress': 'In Arbeit',
        'report.status.non-reproductible': 'Nicht reproduzierbar',
        'report.status.pending': 'Ausstehend',
        'report.status.resolved': 'Gelöst',
        'report.status.wont-fix': 'Nicht behebbar',
        'report.conversation.title': 'Konversation',
        'report.conversation.placeholder': 'Geben Sie eine Nachricht ein (Drücken Sie Shift + Eingabe für eine neue Zeile)...'
    },
    'es-ES': {
        'report-dialog.title': 'Informes',
        'report.properties.status': 'Estado',
        'report.properties.category': 'Categoría',
        'report.properties.title': 'Título',
        'report.properties.description': 'Descripción',
        'report.properties.page-url': 'URL de la página',
        'report.properties.client': 'Cliente',
        'report.properties.user': 'Usuario',
        'report.properties.browser': 'Navegador',
        'report.properties.operating-system': 'Sistema operativo',
        'report.properties.window-size': 'Tamaño de la ventana',
        'report.properties.created-at': 'Creado el',
        'report.category.bug': 'Error',
        'report.category.design': 'Diseño',
        'report.category.question': 'Pregunta',
        'report.category.request': 'Solicitud',
        'report.category.wording': 'Redacción',
        'report.status.closed': 'Cerrado',
        'report.status.in-progress': 'En curso',
        'report.status.non-reproductible': 'No reproducible',
        'report.status.pending': 'Pendiente',
        'report.status.resolved': 'Resuelto',
        'report.status.wont-fix': 'No se corregirá',
        'report.conversation.title': 'Conversación',
        'report.conversation.placeholder': 'Escribe un mensaje (Mayús + Intro para una nueva línea)...'
    },
    'it-IT': {
        'report-dialog.title': 'Segnalazioni',
        'report.properties.status': 'Stato',
        'report.properties.category': 'Categoria',
        'report.properties.title': 'Titolo',
        'report.properties.description': 'Descrizione',
        'report.properties.page-url': 'URL della pagina',
        'report.properties.client': 'Client',
        'report.properties.user': 'Utente',
        'report.properties.browser': 'Browser',
        'report.properties.operating-system': 'Sistema operativo',
        'report.properties.window-size': 'Dimensioni della finestra',
        'report.properties.created-at': 'Creato il',
        'report.category.bug': 'Bug',
        'report.category.design': 'Design',
        'report.category.question': 'Domanda',
        'report.category.request': 'Richiesta',
        'report.category.wording': 'Testo',
        'report.status.closed': 'Chiuso',
        'report.status.in-progress': 'In corso',
        'report.status.non-reproductible': 'Non riproducibile',
        'report.status.pending': 'In attesa',
        'report.status.resolved': 'Risolto',
        'report.status.wont-fix': 'Non verrà corretto',
        'report.conversation.title': 'Conversazione',
        'report.conversation.placeholder': 'Inserisci un messaggio (Maiusc + Invio per una nuova riga)...'
    },
    'pt-PT': {
        'report-dialog.title': 'Relatórios',
        'report.properties.status': 'Estado',
        'report.properties.category': 'Categoria',
        'report.properties.title': 'Título',
        'report.properties.description': 'Descrição',
        'report.properties.page-url': 'URL da página',
        'report.properties.client': 'Cliente',
        'report.properties.user': 'Utilizador',
        'report.properties.browser': 'Browser',
        'report.properties.operating-system': 'Sistema operativo',
        'report.properties.window-size': 'Tamanho da janela',
        'report.properties.created-at': 'Criado em',
        'report.category.bug': 'Erro',
        'report.category.design': 'Design',
        'report.category.question': 'Pergunta',
        'report.category.request': 'Pedido',
        'report.category.wording': 'Redação',
        'report.status.closed': 'Fechado',
        'report.status.in-progress': 'Em curso',
        'report.status.non-reproductible': 'Não reproduzível',
        'report.status.pending': 'Pendente',
        'report.status.resolved': 'Resolvido',
        'report.status.wont-fix': 'Não será corrigido',
        'report.conversation.title': 'Conversação',
        'report.conversation.placeholder': 'Introduza uma mensagem (Shift + Enter para uma nova linha)...'
    },
    'zh-CN': {
        'report-dialog.title': '反馈',
        'report.properties.status': '状态',
        'report.properties.category': '分类',
        'report.properties.title': '标题',
        'report.properties.description': '描述',
        'report.properties.page-url': '页面 URL',
        'report.properties.client': '客户端',
        'report.properties.user': '用户',
        'report.properties.browser': '浏览器',
        'report.properties.operating-system': '操作系统',
        'report.properties.window-size': '窗口大小',
        'report.properties.created-at': '创建时间',
        'report.category.bug': '缺陷',
        'report.category.design': '设计',
        'report.category.question': '问题',
        'report.category.request': '需求',
        'report.category.wording': '文案',
        'report.status.closed': '已关闭',
        'report.status.in-progress': '处理中',
        'report.status.non-reproductible': '无法复现',
        'report.status.pending': '待处理',
        'report.status.resolved': '已解决',
        'report.status.wont-fix': '不予修复',
        'report.conversation.title': '对话',
        'report.conversation.placeholder': '输入消息（按 Shift + Enter 换行）...'
    }
};

export const useTranslations = createSingleton((): {
    languageCode: Ref<Languages>;
    tr: (key: Keys) => string;
} => {
    const languageCode = ref<Languages>('en-US');

    const tr = (key: Keys): string => {
        return translations[languageCode.value][key] || key;
    };

    return {
        languageCode,
        tr
    };
});
